import {
    onAuthStateChanged,
    type ParsedToken,
    signInWithCustomToken,
    type User,
} from "firebase/auth";
import { useEffect, useReducer, useRef } from "react";
import { type AuthContextProps, useAuth } from "react-oidc-context";
import { useHistory } from "react-router-dom";
import App from "./App";
import CapturePreviousLocation from "./CapturePreviousLocation";
import "./components/LoadingSpinner/LoadingSpinner.css";
import { ApiClientProvider, usePost } from "@albertine/api-client";
import { usePostHog } from "posthog-js/react";
import { useKeycloakToken } from "./common/hooks/useKeycloakToken";
import Login from "./components/Login/Login";
import config from "./config";
import { auth } from "./Firebase";
import { queryClient } from "./queryClient";
import { setAnalyticsUserId } from "./utils/analytics.util";
import { ClaimsProvider } from "./utils/claims.util";

// These run at module load so the side effect (stripping `?signedOut=1` from
// the URL) happens before anything else reads `window.location`.
const INITIAL_SIGNED_OUT = consumeSignedOutFlag();
const INITIAL_HAS_AUTH_PARAMS = hasInitialAuthParams();
const INITIAL_RETURN_TO = INITIAL_HAS_AUTH_PARAMS
    ? "/"
    : window.location.pathname + window.location.search + window.location.hash;

type State =
    | {
          authState: "authenticating" | "failed";
          currentUser: null;
          claims: null;
          idToken: null;
      }
    | {
          authState: "done";
          currentUser: User;
          claims: ParsedToken;
          idToken: string;
      };

type Action =
    | { type: "AUTHENTICATING" }
    | { type: "DONE"; user: User; claims: ParsedToken; idToken: string }
    | { type: "FAILED" };

const initialState: State = {
    authState: "authenticating",
    currentUser: null,
    claims: null,
    idToken: null,
};

function reducer(_state: State, action: Action): State {
    switch (action.type) {
        case "AUTHENTICATING":
            return {
                authState: "authenticating",
                currentUser: null,
                claims: null,
                idToken: null,
            };
        case "DONE":
            return {
                authState: "done",
                currentUser: action.user,
                claims: action.claims,
                idToken: action.idToken,
            };
        case "FAILED":
            return {
                authState: "failed",
                currentUser: null,
                claims: null,
                idToken: null,
            };
        default:
            throw new Error("Unknown action type");
    }
}

/**
 * Handles the Keycloak → Firebase auth bridge. Wrapped in ApiClientProvider
 * so it can use the api-client to call the exchange-token endpoint.
 */
const UserContextWithLogin = function UserContextWithLogin() {
    const oidc = useAuth();
    const getKeycloakToken = useKeycloakToken();

    useEffect(() => {
        redirectIfStoredUserExpired(oidc);
    }, [oidc]);

    return (
        <ApiClientProvider
            baseUrl={config.apiServerBaseUrl}
            queryClient={queryClient}
            getFirebaseAuthToken={async () => null}
            getKeycloakToken={getKeycloakToken}
        >
            <AuthBridge />
        </ApiClientProvider>
    );
};

function AuthBridge() {
    const [state, dispatch] = useReducer(reducer, initialState);
    const posthog = usePostHog();
    const oidc = useAuth();
    const history = useHistory();
    const { mutateAsync: exchangeToken } = usePost("exchangeToken");
    const consumedReturnToRef = useRef(false);

    // Exchange the Keycloak token for a Firebase custom token once authenticated.
    useEffect(() => {
        if (state.currentUser !== null) {
            return;
        }

        if (!oidc.isAuthenticated || !oidc.user?.access_token) {
            return;
        }

        async function doExchange() {
            try {
                dispatch({ type: "AUTHENTICATING" });
                const { firebaseToken } = await exchangeToken({});
                await signInWithCustomToken(auth, firebaseToken);
            } catch (error) {
                console.error(
                    "Failed to exchange Keycloak token for Firebase token.",
                    error,
                );
                dispatch({ type: "FAILED" });
            }
        }

        doExchange();
    }, [
        oidc.isAuthenticated,
        oidc.user?.access_token,
        exchangeToken,
        state.currentUser,
    ]);

    // Once Firebase auth state changes, verify the agent and set claims.
    useEffect(() => {
        async function authenticate() {
            if (!auth.currentUser) return;

            setAnalyticsUserId(auth.currentUser.uid);
            posthog.identify(auth.currentUser.uid, {
                email: auth.currentUser.email,
                name: auth.currentUser.displayName,
            });
            const idTokenResult = await auth.currentUser?.getIdTokenResult(true);
            dispatch({
                type: "DONE",
                user: auth.currentUser,
                claims: idTokenResult.claims,
                idToken: idTokenResult.token,
            });
        }

        return onAuthStateChanged(auth, (user) => {
            if (!user) {
                // Reset to neutral state so the render can show the manual Login
                // button after logout instead of a stale "done" / "failed".
                posthog.reset();
                dispatch({ type: "AUTHENTICATING" });
                return;
            }
            authenticate().catch((e: unknown) => {
                dispatch({ type: "FAILED" });
                console.log(
                    "Got an error. Most probably we are in simulator that has not started yet. Waiting for a while. Then retry.",
                    e,
                );

                alert(
                    "Error: Cannot connect to Google Authentication server. Please try to reload page.",
                );
            });
        });
    }, [posthog.identify, posthog.reset]);

    // After a fresh OIDC callback, navigate to the path the user was originally
    // trying to reach. Gated on INITIAL_HAS_AUTH_PARAMS so a stale `returnTo`
    // saved in the localStorage-cached OIDC user (from a previous tab/session)
    // doesn't redirect them on a normal cold load.
    useEffect(() => {
        if (consumedReturnToRef.current) return;
        if (!INITIAL_HAS_AUTH_PARAMS) return;
        if (state.authState !== "done") return;

        consumedReturnToRef.current = true;
        const returnTo = (oidc.user?.state as { returnTo?: string } | undefined)
            ?.returnTo;
        if (returnTo && isSafeReturnTo(returnTo)) {
            history.replace(returnTo);
        }
    }, [state.authState, oidc.user, history]);

    if (state.authState === "done" && oidc.isAuthenticated) {
        return (
            <CapturePreviousLocation>
                <ClaimsProvider
                    claims={state.claims}
                    uid={state.currentUser.uid}
                    idToken={state.idToken}
                >
                    <App currentAgentId={state.currentUser.uid} />
                </ClaimsProvider>
            </CapturePreviousLocation>
        );
    }

    if (oidc.error || state.authState === "failed") {
        return <Login mode="error" returnTo={INITIAL_RETURN_TO} />;
    }

    const inFlight =
        oidc.isLoading ||
        oidc.activeNavigator !== undefined ||
        // OIDC says authenticated but the Firebase exchange hasn't completed.
        (oidc.isAuthenticated && state.authState !== "done") ||
        // Stored OIDC user with an expired token: about to auto-redirect.
        (oidc.user != null && !oidc.isAuthenticated && !INITIAL_SIGNED_OUT);

    if (inFlight) {
        return <Login mode="auto" returnTo={INITIAL_RETURN_TO} />;
    }

    return <Login mode="manual" returnTo={INITIAL_RETURN_TO} />;
}

// Strips a one-time `?signedOut=1` flag set by signoutRedirect's
// post_logout_redirect_uri so subsequent reads of the URL see a clean path.
function consumeSignedOutFlag(): boolean {
    const params = new URLSearchParams(window.location.search);
    if (params.get("signedOut") !== "1") return false;
    params.delete("signedOut");
    const search = params.toString();
    const newUrl =
        window.location.pathname + (search ? `?${search}` : "") + window.location.hash;
    window.history.replaceState(null, "", newUrl);
    return true;
}

function hasInitialAuthParams(): boolean {
    const params = new URLSearchParams(window.location.search);
    return (params.has("code") || params.has("error")) && params.has("state");
}

// Same-origin path check defends against open-redirect attempts via tampered
// OIDC `state`.
function isSafeReturnTo(target: string): boolean {
    return target.startsWith("/") && !target.startsWith("//");
}

function redirectIfStoredUserExpired(oidc: AuthContextProps): void {
    if (oidc.isLoading) return;
    if (oidc.error) return;
    if (oidc.activeNavigator) return;
    if (oidc.isAuthenticated) return;
    if (oidc.user == null) return;
    if (INITIAL_SIGNED_OUT) return;
    void oidc.signinRedirect({ state: { returnTo: INITIAL_RETURN_TO } });
}

export default UserContextWithLogin;
